import { useState, useEffect } from 'react';
import { CircleNotchIcon, PaperPlaneRightIcon, XIcon, ArrowRightIcon } from '@phosphor-icons/react';

const DLP_SPINNER_DELAY_MS = 1000;

interface SendButtonProps {
    numOfLines: number;
    isSending: boolean;
    isStoppable: boolean;
    isDlpChecking?: boolean;
    stopStream: any;
    sendMessageHandler: () => void;
    buttonStyle?: boolean;
    visibilityClass?: string;
    isOverCharacterLimit?: boolean;
    characterLimitTooltip?: string;
}

export default function SendButton({
    numOfLines,
    isSending,
    isStoppable,
    isDlpChecking = false,
    stopStream,
    sendMessageHandler,
    buttonStyle = true,
    visibilityClass = '',
    isOverCharacterLimit = false,
    characterLimitTooltip = '',
}: SendButtonProps) {
    const [showDlpSpinner, setShowDlpSpinner] = useState(false);

    useEffect(() => {
        if (!isDlpChecking) {
            setShowDlpSpinner(false);
            return;
        }
        const t = setTimeout(() => setShowDlpSpinner(true), DLP_SPINNER_DELAY_MS);
        return () => clearTimeout(t);
    }, [isDlpChecking]);

    const desktopButtonClasses =
        'rounded-full h-[46px] w-[46px] transition-all duration-100 text-chatInput-sendButtonColor bg-chatInput-sendButtonBackground border-none flex items-center justify-center';
    const desktopDisabledLook =
        'opacity-50 cursor-not-allowed bg-chatInput-sendButtonBackground';
    const mobileButtonClasses =
        'border-none text-chatInputHome-mobileSendButtonColor rounded-s bg-transparent h-full w-full flex items-center justify-center text-chatInputHome-mobileSendButtonColor';
    const mobileDisabledLook = 'opacity-50 cursor-not-allowed';

    return (
        <>
            {buttonStyle ? (
                <div
                    className={`${visibilityClass} text-layout-textcolor overflow-hidden flex justify-center items-center transition-all h-[56px] w-[56px] duration-300
                        ${numOfLines > 2 ? 'rotate-[-90deg]' : 'rotate-0'}`}
                >
                    {isOverCharacterLimit ? (
                        <div
                            role="button"
                            aria-disabled="true"
                            className={`${desktopButtonClasses} ${desktopDisabledLook} tooltip`}
                            data-tooltip-content={characterLimitTooltip}
                        >
                            <ArrowRightIcon size={24} />
                        </div>
                    ) : (
                        <button
                            className={`${desktopButtonClasses} ${isDlpChecking ? desktopDisabledLook : ''} ${!isDlpChecking ? 'hover:bg-chatInput-sendButtonBackground-hover' : ''}`}
                            onClick={isDlpChecking ? undefined : isSending ? stopStream : () => sendMessageHandler()}
                            disabled={isDlpChecking}
                        >
                            {showDlpSpinner || (isSending && !isStoppable) ? (
                                <CircleNotchIcon size={24} className="animate-spin">
                                    <animateTransform
                                        attributeName="transform"
                                        type="rotate"
                                        from="0 0 0"
                                        to="360 0 0"
                                        dur="1s"
                                        repeatCount="indefinite"
                                    />
                                </CircleNotchIcon>
                            ) : isSending ? (
                                <XIcon size={24} />
                            ) : (
                                <ArrowRightIcon size={24} />
                            )}
                        </button>
                    )}
                </div>
            ) : (
                <div
                    className={`${visibilityClass} h-[60px] overflow-hidden w-[40px] bg-transparent text-layout-textcolor flex justify-center items-center transition-all duration-100 ${
                        numOfLines > 1 ? 'rounded-tl rounded-br' : 'rounded-e-md'
                    } ${buttonStyle ? 'pr-0' : 'pr-2'}`}
                >
                    {isOverCharacterLimit ? (
                        <div
                            role="button"
                            aria-disabled="true"
                            className={`${mobileButtonClasses} ${mobileDisabledLook} tooltip`}
                            data-tooltip-content={characterLimitTooltip}
                        >
                            <PaperPlaneRightIcon size={24} />
                        </div>
                    ) : (
                        <button
                            className={`${mobileButtonClasses} ${isDlpChecking ? mobileDisabledLook : 'cursor-pointer'}`}
                            onClick={isDlpChecking ? undefined : isSending ? stopStream : () => sendMessageHandler()}
                            disabled={isDlpChecking}
                        >
                            {showDlpSpinner || (isSending && !isStoppable) ? (
                                <span className="hover:text-primary transition-all duration-100">
                                    <CircleNotchIcon size={24} className="animate-spin">
                                        <animateTransform
                                            attributeName="transform"
                                            type="rotate"
                                            from="0 0 0"
                                            to="360 0 0"
                                            dur="1s"
                                            repeatCount="indefinite"
                                        />
                                    </CircleNotchIcon>
                                </span>
                            ) : isSending ? (
                                <span className="hover:text-primary transition-all duration-100">
                                    <XIcon size={24} />
                                </span>
                            ) : (
                                <span className="hover:text-primary transition-all duration-100">
                                    <PaperPlaneRightIcon size={24} />
                                </span>
                            )}
                        </button>
                    )}
                </div>
            )}
        </>
    );
}
